'use strict';
const angular = require('angular');
const {copy, isDefined, /*isFunction, */isObject} = angular;
const union = require('lodash/union');

import {DefaultProperties, DefaultRelationProperties} from './domain-properties/Properties';
import {ValidationErrors} from './domain-properties/Validation';

export const DomainClassHelperImport = [
  '$domainValidation',
  '$q',
  '$$DomainUtilities',
  '$domain',
  '$$DomainValues',
  '$$Constraints',
  DomainClassHelper];

function DomainClassHelper(
  $domainValidation,
  $q,
  DomainUtilities,
  $domain,
  $$DomainValues,
  $$Constraints,
) {
  var DomainClassHelper = {};

  //************************************************************************************************************
  // Assign to Domain
  //************************************************************************************************************

  $domain.Constraints = $$Constraints.Constraints;
  $domain.DomainValue = $$DomainValues.DomainValue;
  $domain.DomainValues = $$DomainValues.DomainValues;
  $domain.DefaultProperties = DefaultProperties;
  $domain.DefaultRelationProperties = DefaultRelationProperties;
  $domain.ValidationErrors = ValidationErrors;

  //************************************************************************************************************
  // Misc
  //************************************************************************************************************

  DomainClassHelper.hydrate = function (instance, serializedData) {
    //console.log('hydrating ', serializedData);
    if (isObject(serializedData)) {
      var keys = union(Object.keys(serializedData), Object.keys(instance.$constraints));
      for (var key in keys) {
        var value = serializedData[key];

        // Convert supported Data Types
        if (isDefined(instance.$constraints[key])) {
          instance.$$values[key].hydrate(value);
        } else { // non-persisted fields, such as $id and $ref()
          instance[key] = value;
        }
      }

      if (serializedData.$id) { // this shouldn't actually be needed anymore
        instance.$id = serializedData.$id;
      }
    }
  };

  /**
   * @deprecated Use DomainUtilities.camelCase instead
   */
  DomainClassHelper.camelCase = DomainUtilities.camelCase;

  //************************************************************************************************************
  //        Interceptors TODO: refactor to one implementation method
  //************************************************************************************************************

  /**
   * Callback is invoked with the provided context (or null) immediately prior to validation is performed, when saving.
   * Callback is provided the following arguments:
   * instance: the object being saved.
   * save arguments map: the argument to the $save() method.
   *    Current implemented usage: when you save an instance of a linked collection, mapArgs.'list name' = relational data saved to firebase. defaults to true
   * args: an array of arguments passed as the second argument to $doBeforeSave
   *
   * @param callback
   * @param args
   * @param context
   */
  DomainClassHelper.$doBeforeValidation = function (callback, args, context) {
    var hasArgs = !!args;
    args = args || [];
    this.$$beforeValidationClosures.push(function (instance, argsMap) {
      var thisArgs;
      if (hasArgs) {
        thisArgs = copy(args);
        args.unshift(instance, argsMap);
      } else {
        thisArgs = [instance, argsMap]
      }
      return callback.apply(context, thisArgs);
    });
  };

  /**
   * Callback is invoked with the provided context (or null) immediately prior to saving, after validation has succeeded.
   * Callback is provided the following arguments:
   * instance: the object being saved.
   * save arguments map: the argument to the $save() method.
   *    Current implemented usage: when you save an instance of a linked collection, mapArgs.'list name' = relational data saved to firebase. defaults to true
   * args: an array of arguments passed as the second argument to $doBeforeSave
   *
   * @param callback
   * @param args
   * @param context
   */
  DomainClassHelper.$doBeforeSave = function (callback, args, context) {
    var hasArgs = !!args;
    args = args || [];
    this.$$beforeSaveClosures.push(function (instance, argsMap) {
      var thisArgs;
      if (hasArgs) {
        thisArgs = copy(args);
        args.unshift(instance, argsMap);
      } else {
        thisArgs = [instance, argsMap]
      }
      return callback.apply(context, thisArgs);
    });
  };

  /**
   * Callback is invoked with the provided context (or null) immediately after saving has succeeded.
   * Callback is provided the following arguments:
   * instance: the object being saved.
   * save arguments map: the argument to the $save() method.
   *    Current implemented usage: when you save an instance of a linked collection, mapArgs.'list name' = relational data saved to firebase. defaults to true
   * args: an array of arguments passed as the second argument to $doAfterSave
   *
   * @param callback
   * @param args
   * @param context
   */
  DomainClassHelper.$doAfterSave = function (callback, args, context) {
    var hasArgs = !!args;
    args = args || [];
    this.$$afterSaveClosures.push(function (instance, argsMap) {
      var thisArgs;
      if (hasArgs) {
        thisArgs = copy(args);
        args.unshift(instance, argsMap);
      } else {
        thisArgs = [instance, argsMap]
      }
      return callback.apply(context, thisArgs);
    });
  };

  /**
   * Callback is invoked with the provided context (or null) immediately prior to deletion
   * Callback is provided the following arguments:
   * instance: the object being deleted.
   * delete arguments map: the argument to the $delete() method.
   *
   * @param callback
   * @param args
   * @param context
   */
  DomainClassHelper.$doBeforeDelete = function (callback, args, context) {
    var hasArgs = !!args;
    args = args || [];
    this.$$beforeDeleteClosures.push(function (instance, argsMap) {
      var thisArgs;
      if (hasArgs) {
        thisArgs = copy(args);
        args.unshift(instance, argsMap);
      } else {
        thisArgs = [instance, argsMap]
      }
      return callback.apply(context, thisArgs);
    });
  };

  /**
   * Callback is invoked with the provided context (or null) immediately after deletion has succeeded.
   * Callback is provided the following arguments:
   * instance: the object being deleted.
   * delete arguments map: the argument to the $delete() method.
   *
   * @param callback
   * @param args
   * @param context
   */
  DomainClassHelper.$doAfterDelete = function (callback, args, context) {
    var hasArgs = !!args;
    args = args || [];
    this.$$afterDeleteClosures.push(function (instance, argsMap) {
      var thisArgs;
      if (hasArgs) {
        thisArgs = copy(args);
        args.unshift(instance, argsMap);
      } else {
        thisArgs = [instance, argsMap]
      }
      return callback.apply(context, thisArgs);
    });
  };


  //************************************************************************************************************
  // Save utility methods
  //************************************************************************************************************

  DomainClassHelper.beforeValidation = function (instance, saveArgsMap) {
    return processList(instance.constructor.$$beforeValidationClosures, instance, saveArgsMap);
  };

  DomainClassHelper.beforeSave = function (instance, saveArgsMap) {
    return processList(instance.constructor.$$beforeSaveClosures, instance, saveArgsMap);
  };

  DomainClassHelper.afterSave = function (instance, saveArgsMap) {
    return processList(instance.constructor.$$afterSaveClosures, instance, saveArgsMap);
  };

  DomainClassHelper.beforeDelete = function (instance, deleteArgsMap) {
    return processList(instance.constructor.$$beforeDeleteClosures, instance, deleteArgsMap);
  };

  DomainClassHelper.afterDelete = function (instance, deleteArgsMap) {
    return processList(instance.constructor.$$afterDeleteClosures, instance, deleteArgsMap);
  };

  function processList(list, instance, saveArgsMap) {
    var promises = [];
    var deferred = $q.defer();

    //$window.setTimeout(function () {
    if (list.length) {
      for (var i = 0; i < list.length; i++) {
        var result = list[i](instance, saveArgsMap);
        //if (isObject(result) && isFunction(result.then)) {
        //  promises.push(result);
        //} else {
        promises.push($q.when(result));
        //}
      }

      $q.all(promises).then(
        deferred.resolve.bind(deferred),
        deferred.reject.bind(deferred)
      );
    } else {
      deferred.resolve(null);
    }
    //});

    return deferred.promise;
  }


  //************************************************************************************************************
  // Generic Interception
  //************************************************************************************************************

  /**
   * @deprecated Use DomainUtilities.intercepted instead
   */
  DomainClassHelper.intercepted = DomainUtilities.intercepted;


  //************************************************************************************************************
  // Data Validation // TODO: refactor to object for more flexibility
  //************************************************************************************************************

  DomainClassHelper.validateConstraint = function validateConstraint(instance, field) {
    var value, errors;
    var className = instance.constructor.$name;

    if (instance.$constraints[field].type === 'Domain') {
      value = instance.$getLink(field);
    } else {
      value = instance[field];
    }

    if (instance.$constraints[field].type === 'NestedDocument') {
      instance.$$values[field].originalValue.$validate();
      if (instance.$$values[field].originalValue.$hasErrors()) {
        errors = instance.$$values[field].originalValue.$errors.toArray();
      }
    } else {
      errors = $domainValidation.validateConstraint(value, instance.$constraints[field], null, className, field, instance);
    }

    if (instance && errors.length > 0) {
      instance.$errors.$addErrors(field, errors);
    }
  };

  //Validate only whether the value is the correct type.
  DomainClassHelper.validateType = function validateType(instance, field, className) {
    var value;

    if (instance.$constraints[field].type === 'Domain') {
      value = instance.$getLink(field);
    } else {
      value = instance[field];
    }

    var errors = $domainValidation.validateType(value, instance.$constraints[field], [], className, field);

    if (instance && errors.length > 0) {
      if (instance && errors.length > 0) {
        instance.$errors.$addErrors(field, errors);
      }
    }
  };

  DomainClassHelper.validateValue = function validateValue(instance, field, className) {
    var value;
    var constraint = instance.$constraints[field];

    if (constraint.type === 'Domain') {
      value = instance.$getLink(field);
    } else {
      value = instance[field];
    }

    var errors = $domainValidation.validateValue(value, instance.$constraints[field], [], className, field);

    if (instance && errors.length > 0) {
      if (instance && errors.length > 0) {
        instance.$errors.$addErrors(field, errors);
      }
    }
  };

  return DomainClassHelper;
}


// WEBPACK FOOTER //
// ./src/js/_libs/angular-domain/DomainClassHelper.js